import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSlate } from 'slate-react';
import { Transforms, Element as SlateElement } from 'slate';
import { ConfirmationModal } from 'app/components';

import { useGetDocumentTableOfContent, SectionEntry, SectionTargetLanguage } from 'entities/document';
import { calculatePageOffset, getBookmarkAndLinks } from 'slate/components/page/utils';
import { useEditorNavigate, useEditorSearch } from 'app/router/params';
import { PageVariables } from './PageVariables';
import { useDraggableList } from 'app/utils/hooks/useDraggableList';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { useDeletePage } from 'entities/document';
import { deletePageAt } from 'slate/extensions/page';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse, useEditorLiveSection, useEditorUiSetters } from 'app/state/contexts/EditorUiContext';
interface PageNode {
  uuid?: string;
  type?: string;
  [key: string]: unknown;
}

interface ActiveSection {
  uuid?: string;
  type?: string;
}

/**
 * Check if activeSection uuid equals page uuid.
 * If activeSection has no uuid assume the first page is selected.
 */
const isPageActive = (page: PageNode, activeSection: ActiveSection | null | undefined) => {
  if (activeSection?.uuid) {
    return page.uuid === activeSection.uuid;
  }

  return false;
};

interface DocumentPagesProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  readOnly?: boolean;
}

export const DocumentPages = ({ containerRef, readOnly }: DocumentPagesProps) => {
  const section = useEditorLiveSection();
  const activeSection = useEditorCoarse().activeSection;
  const { setActiveSection } = useEditorUiSetters();
  const navigate = useEditorNavigate();
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const handleClickPage = (index: number) => {
    const clickedPage = section.content[index];
    navigate({ hash: clickedPage.uuid, search: true });
    setActiveSection(clickedPage);
  };

  useEffect(() => {
    const element = document.querySelector(`#preview-${activeSection?.uuid}`) as HTMLElement | null;
    if (element && shouldScroll(element, containerRef.current)) {
      const yOffset = -61; // offset for the sticky header
      const y = element.offsetTop - yOffset;
      containerRef.current?.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [activeSection, containerRef]);

  if (selectedPage !== null) {
    return <PageVariables selectedPage={selectedPage} setSelectedPage={setSelectedPage} />;
  } else {
    return <PageOverview onClick={handleClickPage} onSelectPage={setSelectedPage} readOnly={readOnly} />;
  }
};

interface PageOverviewProps {
  onClick: (index: number, page?: PageNode) => void;
  readOnly?: boolean;
  onSelectPage: (pageNumber: number) => void;
}

const PageOverview = ({ onClick, readOnly, onSelectPage }: PageOverviewProps) => {
  const { t } = useTranslation('doc-editor');

  const editor = useSlate();
  const locale = useEditorLocale();
  const section = useEditorLiveSection();
  const activeSection = useEditorCoarse().activeSection;
  const translationId = useEditorSearch().translation;
  const { data: pDocument } = useEditorDocument();
  const [pages, setPages] = useState<PageNode[]>([]);
  const { data } = useGetDocumentTableOfContent(pDocument.revisionId, {
    locale,
    sectionSuperId: section.superId,
    sectionRevisionId: translationId ?? section.revisionId,
  }) as { data?: { range: { start: number; stop: number } } };
  const pageOffset = calculatePageOffset(pDocument.content?.pageNumbering);
  const sectionTitle = pDocument?.content?.sections.find((s: SectionEntry) => {
    if (s.superId === section.superId) {
      return s;
    } else {
      return s.targetLanguages?.find((tl: SectionTargetLanguage) => tl.revisionId === section.revisionId);
    }
  })?.name;

  const {
    reorderedList: sortedPages,
    draggedItem,
    draggableAttributes,
  } = useDraggableList(editor.children as PageNode[], (reorderedList) => {
    const fromIndex = pages.findIndex((page) => page.uuid === draggedItem?.uuid);
    const toIndex = reorderedList.findIndex((page) => page.uuid === draggedItem?.uuid);
    Transforms.select(editor, [toIndex]);
    Transforms.moveNodes(editor, { at: [fromIndex], to: [toIndex], match: (node) => SlateElement.isElement(node) && node.type === 'page' });
    dispatchMessage(EventType.ADD_MESSAGE, {
      type: 'success',
      title: t('sidebar.page.success.title'),
      text: `${t('sidebar.page.success.desc')} ${toIndex + 1}`,
    });
    setPages(reorderedList);
  });

  useEffect(() => {
    setPages(editor.children);
  }, [editor.children]);

  if (data == null) {
    return null;
  }
  const startPage = data.range.start + pageOffset;
  const endPage = data.range.stop + pageOffset;
  return (
    <div className="preview--container">
      <div className="d-flex justify-content-between px-3 pt-4 align-items-center pb-3 w-100 bg-white sticky-top">
        <div className="fw-bold">{sectionTitle}</div>
        <div className="text-gray-800 fs-7">
          {t('sidebar.page-prefix')}
          {startPage > 0 ? startPage : 0}-{endPage}
        </div>
      </div>
      <div className="px-3 w-100">
        {!pages.length
          ? null
          : (sortedPages || pages).map((page, index) => (
              <div
                key={index}
                id={`preview-${page.uuid}`}
                className={`rounded cursor-pointer px-3 py-2 mb-2 ${page.uuid === draggedItem?.uuid ? 'opacity-25' : ''} ${
                  isPageActive(page, activeSection) ? 'active bg-blue-200 border-blue-800 text-blue-800' : ''
                }`}
                onClick={() => onClick(index, page)}
                {...draggableAttributes(page)}
              >
                <PagePreview
                  index={index}
                  readOnly={readOnly}
                  pageNumber={startPage + index}
                  page={page}
                  active={page.uuid === activeSection?.uuid}
                  onSettingsClick={onSelectPage}
                />
              </div>
            ))}
      </div>
    </div>
  );
};

interface PagePreviewProps {
  index: number;
  readOnly?: boolean;
  active?: boolean;
  pageNumber: number;
  page?: PageNode;
  onSettingsClick: (pageNumber: number) => void;
}

const PagePreview = ({ index, readOnly, active = false, pageNumber, onSettingsClick }: PagePreviewProps) => {
  const { t } = useTranslation('doc-editor');

  const editor = useSlate();
  const { data: pDocument } = useEditorDocument();
  const section = useEditorLiveSection();
  const { sectionPermissions } = usePermissions(pDocument.superId);

  const { mutate: deletePage } = useDeletePage({ documentSuperId: pDocument.superId });

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  const handleDeletePage = () => {
    const page = editor.children[index] as PageNode;
    const idMappings = getBookmarkAndLinks(editor.children[index]);

    deletePage(
      { pageId: page.uuid ?? '', sectionSuperId: section.superId, idMappings },
      {
        onSuccess: () => {
          deletePageAt(editor, index);
        },
      }
    );
    setShowDeleteConfirmationModal(false);
  };

  const handleClickDelete = () => {
    setShowDeleteConfirmationModal(true);
  };

  const handleSettingsClick = () => {
    onSettingsClick(pageNumber);
  };

  const pageOptions = [
    {
      icon: 'sliders',
      text: t('sidebar.page.link.settings'),
      disabled: readOnly,
      onClick: active ? handleSettingsClick : null,
    },
    {
      icon: 'trash',
      text: t('sidebar.page.link.delete'),
      disabled: readOnly || editor.children.length < 2 || sectionPermissions[section.superId] !== 'WRITE',
      onClick: active ? handleClickDelete : null,
    },
  ];

  return (
    <>
      <div className="d-flex w-full justify-content-between">
        <button className="bg-transparent border-0 draggable-handle  d-flex align-items-center">
          <i className="fa-solid fa-grip-vertical text-blue-500 pe-2" />
          <div>{pageNumber < 1 ? '-' : pageNumber}</div>
        </button>
        <div>
          {active && !readOnly
            ? pageOptions?.map((l, i) => {
                return (
                  <button
                    key={i}
                    disabled={l.disabled}
                    className={`bg-transparent ms-2 border-0 ${l.disabled ? 'text-disabled' : ''}`}
                    onClick={() => l.onClick?.()}
                  >
                    <i className={`fa-regular fa-${l.icon} ${l.disabled ? 'text-disabled' : 'text-gray-800'}`} />
                    <span className="ms-2">{l.text}</span>
                  </button>
                );
              })
            : null}
        </div>
      </div>
      <ConfirmationModal
        message={
          <>
            <i className="fa-regular fa-trash-can fs-1 mb-4" />
            <h3 className="confirm-delete-header">{t('confirm-delete')}</h3>
            <span className="text-gray-900 fs-7">{t('confirm-delete-info')}</span>
          </>
        }
        title={' '}
        show={showDeleteConfirmationModal}
        onConfirm={handleDeletePage}
        onDecline={() => setShowDeleteConfirmationModal(false)}
      />
    </>
  );
};

const shouldScroll = (element: HTMLElement | null, scrollElement: HTMLElement | null) => {
  if (element == null || scrollElement == null) {
    return false;
  }

  const parentHeight = scrollElement.clientHeight;
  const elementTop = element.getBoundingClientRect().top;
  const elementBottom = element.getBoundingClientRect().bottom;

  if (elementTop < 0) {
    return true;
  }

  if (elementBottom > parentHeight) {
    return true;
  }

  return false;
};
